// Tax calculators: take-home pay, income tax, capital gains, sales tax.
import { round2 } from "./finance-math";
import { estimateTax } from "../tax";
import { PAYROLL, SALES_TAX } from "../financial-data/canada";

function cppPremium(gross) {
  const p = PAYROLL.cpp;
  const base = Math.min(Math.max(0, gross - p.basicExemption), Math.max(0, p.ympe - p.basicExemption)) * p.rate;
  const cpp2 = Math.min(Math.max(0, gross - p.ympe), Math.max(0, p.yampe - p.ympe)) * p.cpp2Rate;
  return base + cpp2;
}
function eiPremium(gross) {
  return Math.min(Math.max(0, gross), PAYROLL.ei.mie) * PAYROLL.ei.rate;
}

export function takeHomePay({ grossAnnual, province = "ON", payFrequency = 26, rrspContribution = 0 }) {
  const gross = Math.max(0, Number(grossAnnual) || 0);
  const rrsp = Math.max(0, Number(rrspContribution) || 0);
  const taxable = Math.max(0, gross - rrsp);
  const tax = estimateTax(taxable, province);
  const cpp = cppPremium(gross);
  const ei = eiPremium(gross);
  const netAnnual = gross - tax.totalTax - cpp - ei;
  const freq = Number(payFrequency) || 26;
  return {
    netAnnual: round2(netAnnual),
    netPerPay: round2(netAnnual / freq),
    grossPerPay: round2(gross / freq),
    incomeTax: round2(tax.totalTax),
    cpp: round2(cpp),
    ei: round2(ei),
    totalDeductions: round2(gross - netAnnual),
    averageRate: gross ? round2(((gross - netAnnual) / gross) * 100) : 0,
    marginalRate: round2(tax.marginalRate * 100),
  };
}

export function incomeTax({ income, province = "ON", rrspDeduction = 0 }) {
  const taxable = Math.max(0, (Number(income) || 0) - (Number(rrspDeduction) || 0));
  const t = estimateTax(taxable, province);
  return {
    federalTax: round2(t.federalTax), provincialTax: round2(t.provincialTax),
    totalTax: round2(t.totalTax), afterTax: round2((Number(income) || 0) - t.totalTax),
    averageRate: round2(t.averageRate * 100), marginalRate: round2(t.marginalRate * 100),
  };
}

export function capitalGains({ proceeds, acb, otherIncome, province = "ON", inclusionRate = 50 }) {
  const gain = Math.max(0, (Number(proceeds) || 0) - (Number(acb) || 0));
  const incl = gain * (Math.max(0, Number(inclusionRate) || 0) / 100);
  const base = estimateTax(Number(otherIncome) || 0, province);
  const withGain = estimateTax((Number(otherIncome) || 0) + incl, province);
  const taxOnGain = withGain.totalTax - base.totalTax;
  return {
    capitalGain: round2(gain),
    taxableGain: round2(incl),
    taxOwing: round2(taxOnGain),
    afterTaxGain: round2(gain - taxOnGain),
    effectiveRate: gain ? round2((taxOnGain / gain) * 100) : 0,
    marginalRate: round2(withGain.marginalRate * 100),
  };
}

export function salesTax({ amount, province = "ON" }) {
  const p = SALES_TAX.provinces[province] || SALES_TAX.provinces.ON;
  const amt = Math.max(0, Number(amount) || 0);
  const tax = amt * (p.total / 100);
  return {
    subtotal: round2(amt), taxRate: p.total, taxType: p.type,
    taxAmount: round2(tax), total: round2(amt + tax),
    gst: round2(amt * (p.gst / 100)), pst: round2(amt * (p.pst / 100)),
    provinceName: p.name,
  };
}
